import useRefDimensions from "@/hooks/useRefDimensions";
import { BIG_POPUP_HEIGHT, BIG_POPUP_WIDTH } from "@/theme/primary";
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Modal, ModalProps, Stack, SxProps, Typography } from "@mui/material";
import { useRef } from "react";

interface IPopup extends ModalProps {
  /**
   * Optional size configuration for the modal.
   *
   * @type {Object}
   * @property {string | number} [width] - The width of the modal.
   * @property {string | number} [height] - The height of the modal.
   */
  modalSize?: { width: string | number; height: string | number };

  /**
   * Optional custom styling for the modal.
   *
   * @type {SxProps}
   */
  customStyle?: SxProps;

  /**
   * The title to be displayed at the top of the modal.
   *
   * @type {string}
   */
  title: string;

  /**
   * Callback function to be called when the modal is requested to close.
   *
   * @callback
   */
  onClose: () => void;

  /**
   * To display buttons at the bottom of the popup
   */
  actions?: React.ReactNode;
}

/**
 * A centered popup that can be customized. It closes itself on click away.
 * @param onClose : Function to call to close the modal
 * @param modalSize : width and height of the modal
 * @param title : Mandatory title of the popup to position it on the top left with a close icon on the right
 * @param customStyle : A sx object to customize popup style.
 * @returns A popup that open when open props is true
 */
const Popup: React.FC<IPopup> = ({ onClose, modalSize, title, actions, customStyle, ...rest }) => {
  const actionsRef = useRef(null);
  const { height: actionsHeight } = useRefDimensions(actionsRef);

  const titleRef = useRef(null);
  const { height: titleHeight } = useRefDimensions(titleRef);

  return (
    <Modal onClose={onClose} {...rest}>
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          ...modalSize,
          backgroundColor: (theme: ThemeType) => theme.palette.primary.contrastText,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          position: "absolute",
          borderRadius: 3,
          ...customStyle,
        }}>
        <Stack height="100%" justifyContent="space-between">
          <Stack
            ref={titleRef}
            sx={{
              borderTopLeftRadius: "12px",
              borderTopRightRadius: "12px",
              background: (theme) => theme.palette.primary.main,
              textAlign: "center",
              color: (theme: ThemeType) => theme.palette.primary.contrastText,
            }}
            direction="row"
            width="100%"
            justifyContent="space-between"
            alignItems="center">
            <Typography
              variant="h6"
              sx={{ textAlign: "center", width: "100%", padding: "16px 0" }}
              color="text.contrast">
              {title}
            </Typography>
            <Box>
              <IconButton onClick={onClose}>
                <CloseIcon sx={{ color: (theme: ThemeType) => theme.palette.primary.contrastText }} />
              </IconButton>
            </Box>
          </Stack>
          <Box
            height={`calc(100% - ${titleHeight}px - ${actionsHeight}px)`}
            sx={{
              overflowY: "auto",
              minHeight: `calc(100% - ${titleHeight}px - ${actionsHeight}px)`,
              padding: 2,
              boxSizing: "border-box",
            }}>
            {rest.children}
          </Box>
          <Stack
            sx={{ boxSizing: "border-box", padding: "8px" }}
            justifyContent="center"
            alignItems="center"
            ref={actionsRef}>
            {actions ? (
              <Box width="100%" padding={1}>
                {actions}
              </Box>
            ) : null}
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};

Popup.defaultProps = {
  modalSize: { width: BIG_POPUP_WIDTH, height: BIG_POPUP_HEIGHT },
  customStyle: {},
};

export default Popup;
